import { useCallback, useState } from "react";

import { uploadFile } from "../../../services/upload";

export type UseAttachedFilesOptions = {
  onUploadError?: (error: unknown) => void;
};

export type AttachmentItem = {
  id: string;
  file: File;
  status: "pending" | "done" | "error";
  attachment?: {
    key: string;
    source: string;
    type: "image" | "video" | "file";
    name?: string;
  };
};

function deriveAttachmentType(file: File): "image" | "video" | "file" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "file";
}

export function useAttachedFiles(options: UseAttachedFilesOptions = {}) {
  const { onUploadError } = options;
  const [attachedItems, setAttachedItems] = useState<AttachmentItem[]>([]);

  const addFiles = useCallback(
    (files: File[]) => {
      const newItems: AttachmentItem[] = files.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        status: "pending" as const,
      }));
      setAttachedItems((prev) => [...prev, ...newItems]);

      newItems.forEach((item) => {
        uploadFile(item.file)
          .then((r) => {
            setAttachedItems((prev) =>
              prev.map((p) =>
                p.id === item.id
                  ? {
                      ...p,
                      status: "done" as const,
                      attachment: {
                        key: r.key,
                        source: r.location,
                        type: deriveAttachmentType(p.file),
                        name: p.file.name,
                      },
                    }
                  : p,
              ),
            );
          })
          .catch((err) => {
            onUploadError?.(err);
            setAttachedItems((prev) =>
              prev.map((p) =>
                p.id === item.id ? { ...p, status: "error" as const } : p,
              ),
            );
          });
      });
    },
    [onUploadError],
  );

  const addFile = useCallback(
    (file: File) => {
      addFiles([file]);
    },
    [addFiles],
  );

  const removeFile = useCallback((index: number) => {
    setAttachedItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearAll = useCallback(() => {
    setAttachedItems([]);
  }, []);

  const readyAttachments = attachedItems
    .filter((item) => item.status === "done" && item.attachment)
    .map((item) => item.attachment!);

  const hasPending = attachedItems.some((item) => item.status === "pending");
  const hasError = attachedItems.some((item) => item.status === "error");

  return {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
    hasError,
  };
}
